import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  Button,
  DocumentListCard,
  Unifree
} from '@yes.technology/react-toolkit'

import ItemGroup from './components/ItemGroup'
import InteractionData from './components/InteractionData'
import GoToTop from './components/GoToTop/GoToTop'
import { useReviewSaveButtonContext } from './components/ReviewSaveButton/ReviewSaveButtonContext'
import { FlatInteractionModelData, InteractionModel } from 'types/shared'
import { ReviewContextProvider } from './context'

const useToggleStickySave = () => {
  const staticSaveRef = useRef<HTMLDivElement>(null)
  const { setStickySaveVisible } = useReviewSaveButtonContext()

  useEffect(() => {
    if (!staticSaveRef.current) {
      return
    }

    // show sticky save button if static save button is not fully visible
    const staticSaveObserver = new IntersectionObserver(
      (entries) => {
        setStickySaveVisible(entries[0].intersectionRatio !== 1)
      },
      { threshold: 1 }
    )

    const staticSaveButton = staticSaveRef.current
    staticSaveObserver.observe(staticSaveButton)

    return () => {
      staticSaveObserver.unobserve(staticSaveButton)
    }
  }, [setStickySaveVisible])

  return {
    staticSaveRef
  }
}

interface ReviewProps {
  onSave?: () => void
  goToStep?: (step: number) => void
  flatData: FlatInteractionModelData
  userName: string
  dateStartInteraction: string
  geolocation: InteractionModel['geolocation']
  reviewActivity?: boolean
  displayedActivityModels?: string[]
}

const Review = ({
  onSave,
  goToStep,
  flatData,
  userName,
  dateStartInteraction,
  geolocation,
  reviewActivity,
  displayedActivityModels
}: ReviewProps) => {
  const { t } = useTranslation()

  const [currentCoords, setCurrentCoords] = useState<GeolocationCoordinates>()

  const { staticSaveRef } = useToggleStickySave()

  const reviewContextValue = {
    flatData,
    user: userName,
    dateStartInteraction,
    geolocation,
    reviewActivity,
    displayedActivityModels
  }

  const steps = flatData.interaction_model_item_groups || []

  return (
    <ReviewContextProvider value={reviewContextValue}>
      <div className='position-relative pb-5'>
        {!reviewActivity && (
          <>
            <DocumentListCard className='mt-2'>
              <Unifree
                disabled
                heightClass='large'
                label={t('step.review.status')}
                // TODO: Remove hardcoded value
                value={t('step.review.waiting-activation')}
                required
              />
            </DocumentListCard>

            <DocumentListCard className='mt-2'>
              <Unifree
                disabled
                heightClass='large'
                // TODO: Update textg to 'ID | Situation | Version' in i18n's json
                label={t('step.review.id-situation-version')}
                // TODO: Remove hardcoded value
                value={t('step.review.in-insertion')}
                required
              />
              <div ref={staticSaveRef}>
                <Button
                  className='mt-3'
                  onClick={onSave}
                  impactClass='situationChange'
                >
                  {t('step.review.save-and-activate')}
                </Button>
              </div>
            </DocumentListCard>

            <InteractionData
              coords={currentCoords}
              onCoordsChange={setCurrentCoords}
              dateStartInteraction={dateStartInteraction}
              userName={userName}
            />
          </>
        )}

        <ItemGroup itemGroups={steps} goToStep={goToStep} />

        <GoToTop />
      </div>
    </ReviewContextProvider>
  )
}

export default Review
